import type { ReactNode } from "react";
import { LoaderCircle, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

export type StoryEditorSaveState = "error" | "idle" | "saved" | "saving";

type StoryWorkspaceHeaderProps = {
  actions?: ReactNode;
  description: string;
  metaBadges?: string[];
  modeLabel: string;
  saveError: string | null;
  saveState: StoryEditorSaveState;
  title: string;
};

export function StoryWorkspaceHeader({
  actions,
  description,
  metaBadges = [],
  modeLabel,
  saveError,
  saveState,
  title,
}: StoryWorkspaceHeaderProps) {
  const { t } = useTranslation();

  return (
    <section className="space-y-4 rounded-[30px] border border-white/70 bg-card/90 p-6 shadow-[0_22px_70px_-34px_rgba(40,31,19,0.44)]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            {t("story.workspace.eyebrow")}
          </p>
          <h2 className="text-3xl text-foreground">{title}</h2>
          <p className="max-w-3xl leading-7 text-muted-foreground">{description}</p>
        </div>
        <div className="space-y-2">
          {actions ? <div className="flex justify-end">{actions}</div> : null}
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-2 text-sm text-secondary-foreground">
            {saveState === "saving" ? (
              <>
                <LoaderCircle className="h-4 w-4 animate-spin" />
                {t("story.save.saving")}
              </>
            ) : saveState === "error" ? (
              <>
                <TriangleAlert className="h-4 w-4" />
                {t("story.save.error")}
              </>
            ) : saveState === "saved" ? (
              t("story.save.saved")
            ) : (
              t("story.save.ready")
            )}
          </div>
          <div className="text-right text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {modeLabel}
          </div>
        </div>
      </div>

      {metaBadges.length ? (
        <div className="flex flex-wrap gap-2">
          {metaBadges.map((badge) => (
            <div
              key={badge}
              className="rounded-full bg-primary/10 px-3 py-1 text-sm text-foreground"
            >
              {badge}
            </div>
          ))}
        </div>
      ) : null}

      {saveError ? (
        <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {saveError}
        </p>
      ) : null}
    </section>
  );
}
